import React, { useRef } from 'react';
import { useMask } from '@react-input/mask';
import { useIsMobile } from '../hooks/useIsMobile';
import Altcha from './Altcha';
import { Sys_Data_Type_Number, Sys_Data_Type_Decimal, Sys_Data_Type_Currency, Sys_Data_Type_DateTime, Sys_Data_Type_Date, Sys_Data_Type_Phone, Sys_Data_Type_Email, Sys_Data_Type_USState } from "../store/constants/systemConstants";
import "../styles/index.css";

const FormRenderer = ({ formJson, onSubmit, formSubmissionLoading }) => {

  const isMobile = useIsMobile(550);
  const altchaRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const phoneInputRef = useMask({
    mask: '(___) ___-____',
    replacement: { _: /\d/ },
  });

  // Add this function instead
  const createPhoneMask = (fieldId) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMask({
      mask: '(___) ___-____',
      replacement: { _: /\d/ },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {};

    formData.forEach((value, key) => {
      // Skip the altcha field
      if (key !== 'altcha') {
        // Find the input element
        const input = e.target.querySelector(`[name="${key}"]`);

        // Check if it's a phone input
        if (input && input.type === 'tel') {
          // Remove all non-digit characters from phone number
          data[key] = value.replace(/\D/g, '');
        } else {
          data[key] = value;
        }
      }
    });

  
    if (formJson?.extraParams && typeof formJson?.extraParams === 'object') {
      Object.assign(data, formJson.extraParams);
    }

    onSubmit(data);
  };

  const renderInputControls = (field) => {
    if (field?.sys_data_type_id) {

      // Number, Decimal and Currency
      if (field.sys_data_type_id === Sys_Data_Type_Number || field.sys_data_type_id === Sys_Data_Type_Decimal || field.sys_data_type_id === Sys_Data_Type_Currency) {
        return (
          <>
            <label htmlFor={field.id} style={field.styles.label}>
              {field.label}
            </label>
            <input
              id={field.id}
              type="number"
              name={field.name}
              placeholder={field.placeholder}
              required={field.required}
              style={field.styles.input}
              onInvalid={(e) => e.target.setCustomValidity('Required')}
              onInput={(e) => e.target.setCustomValidity('')}
            />
          </>
        );
      }

      // Phone
      if (field.sys_data_type_id === Sys_Data_Type_Phone) {
        const phoneRef = createPhoneMask(field.id);
        return (
          <>
            <label htmlFor={field.id} style={field.styles.label}>
              {field.label}
            </label>
            <input
              ref={phoneRef}
              id={field.id}
              type="tel"
              name={field.name}
              // placeholder={field.placeholder}
              placeholder="(123) 456-789"
              required={field.required}
              style={field.styles.input}
              onInvalid={(e) => e.target.setCustomValidity('Required')}
              onInput={(e) => e.target.setCustomValidity('')}
            />
          </>
        );
      }

      // Email
      if (field.sys_data_type_id === Sys_Data_Type_Email) {
        return (
          <>
            <label htmlFor={field.id} style={field.styles.label}>
              {field.label}
            </label>
            <input
              id={field.id}
              type="email"
              name={field.name}
              placeholder={field.placeholder}
              required={field.required}
              style={field.styles.input}
              onInvalid={(e) => {
                const email = e.target.value;
                // Updated regex to require proper domain with TLD
                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/;
                if (!email) {
                  e.target.setCustomValidity('Email is required');
                } else if (!emailRegex.test(email)) {
                  e.target.setCustomValidity('Please enter a valid email address');
                }
              }}
              onInput={(e) => {
                const email = e.target.value;
                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/;
                if (!emailRegex.test(email)) {
                  e.target.setCustomValidity('Please enter a valid email address');
                } else {
                  e.target.setCustomValidity('');
                }
              }}
            />
          </>
        );
      }

      // US State Dropdown
      // if (field.sys_data_type_id === Sys_Data_Type_USState) {
      //   return (
      //     <>
      //       <label htmlFor={field.id} style={field.styles.label}>
      //         {field.label}
      //       </label>
      //       <input
      //         id={field.id}
      //         type="number"
      //         name={field.name}
      //         placeholder={field.placeholder}
      //         required={field.required}
      //         style={field.styles.input}
      // onInvalid={(e) => e.target.setCustomValidity('Required')}
      // onInput={(e) => e.target.setCustomValidity('')}
      //       />
      //     </>
      //   );
      // }

      // US State Dropdown
      if (field.sys_data_type_id === Sys_Data_Type_USState) {
        return (
          <>
            <label htmlFor={field.id} style={field.styles.label}>
              {field.label}
            </label>
            <select
              id={field.id}
              name={field.name}
              // Temporary fix for the width issue
              // style={{...field.styles.input, width: `calc(${field.styles.input.width} + 23px)`}} 
              style={{ ...field.styles.input, width: `calc(${field.styles.input.width} + 23px)` }}
              required={field.required}
              onInvalid={(e) => e.target.setCustomValidity('Required')}
              onInput={(e) => e.target.setCustomValidity('')}>
              <option value="" disabled={field.required}>
                Select {field.label}
              </option>
              {formJson?.activeStateList?.map((state) => (
                <option key={state.sys_us_state_id} value={state.state_abbreviation}>
                  {state.state_abbreviation}
                </option>
              ))}
            </select>
          </>
        );
      }


      // DateTime
      if (field.sys_data_type_id === Sys_Data_Type_DateTime) {
        return (
          <>
            <label htmlFor={field.id} style={field.styles.label}>
              {field.label}
            </label>
            <input
              id={field.id}
              type="datetime-local"
              name={field.name}
              placeholder={field.placeholder}
              required={field.required}
              style={field.styles.input}
              onInvalid={(e) => e.target.setCustomValidity('Required')}
              onInput={(e) => e.target.setCustomValidity('')}
            />
          </>
        );
      }

      // Only Date
      if (field.sys_data_type_id === Sys_Data_Type_Date) {
        return (
          <>
            <label htmlFor={field.id} style={field.styles.label}>
              {field.label}
            </label>
            <input
              id={field.id}
              type="date"
              name={field.name}
              placeholder={field.placeholder}
              required={field.required}
              style={field.styles.input}
              onInvalid={(e) => e.target.setCustomValidity('Required')}
              onInput={(e) => e.target.setCustomValidity('')}
            />
          </>
        );
      }


      // All other types as text
      return (
        <>
          <label htmlFor={field.id} style={field.styles.label}>
            {field.label}
          </label>
          <input
            id={field.id}
            type="text"
            name={field.name}
            placeholder={field.placeholder}
            required={field.required}
            style={field.styles.input}
            onInvalid={(e) => e.target.setCustomValidity('Required')}
            onInput={(e) => e.target.setCustomValidity('')}
          />
        </>
      );

    } else {
      return null;
    }

  };

  const RenderFormPreview = () => {
    return (
      <div
        style={{
          flex: 1,
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {formJson.rows.map(row => (
          <div key={row.id}>
            <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
              {row.columns.map(column => (
                <div
                  key={column.id}
                  style={{
                    padding: '4px',
                    flex: 1,
                    borderRadius: '8px',
                  }}
                >
                  {column.fields.map(field => (
                    <div
                      key={field.id}
                      style={{
                        marginBottom: '10px',
                        padding: '8px',
                      }}
                    >
                      {renderInputControls(field)}

                      {field.type === 'complianceText' ? (
                        <article
                          style={{
                            ...field.styles?.complianceText,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'pre-wrap',
                            maxWidth: '100%',
                          }}
                          dangerouslySetInnerHTML={{ __html: field.label }}
                        >
                        </article>
                      ) : null}

                      {field.type === 'submit' ? (
                        <>
                          <button type={field.type} style={{
                            ...field.styles?.button,
                            cursor: formSubmissionLoading ? 'not-allowed' : 'pointer',
                            opacity: formSubmissionLoading ? 0.7 : 1
                          }}
                            disabled={formSubmissionLoading}>
                            {field.label}
                          </button>
                          <div style={{ marginTop: "20px", maxWidth: "230px" }}>
                            <Altcha
                              ref={altchaRef}
                            />
                          </div>
                          {/* <div style={{ display: 'flex', flexDirection: 'row-reverse', marginTop: "20px" }}>
                            <div style={{ minWidth: "260px" }}>
                              <Altcha
                                ref={altchaRef}
                              />
                            </div>
                          </div> */}
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          // height: '100vh',
        }}
      >
        <div style={{ display: 'flex', flex: 1, width: '100%' }}>{RenderFormPreview()}</div>
      </div>
    </form>
  );
};

export default FormRenderer;